import React, {
  useState, useEffect,
} from "react";
import {
  Input, Spin, Button, AutoComplete,
} from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import "./PesticideCompounds.css";
import { toast } from "react-toastify";
import { getPesticideCompounds, deletePesticideCompounds, savePesticideCompounds } from "../../../../actions/batchReports";
import { getUser } from "../../../../utils/helpers";

const maxCompoundNameLength = 200;
const Header = ({ onSearch, compounds }) => {
  const handleSearchOnChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      onSearch(e.target.value);
    }
  };

  return ((
    <div className="PesticideCompounds__Header">
      <AutoComplete
        options={compounds.map((item) => ({ value: item.compound }))}
        filterOption={(inputValue, option) => option.value?.toLowerCase().startsWith(inputValue?.toLowerCase())}
        onSelect={(value) => onSearch(value)}
      >
        <Input.Search
          placeholder="Search"
          className="PesticideCompounds__SearchFilter"
          onPressEnter={(e) => onSearch(e.target.value)}
          onChange={(e) => handleSearchOnChange(e)}
          onSearch={(value) => onSearch(value)}
          allowClear
        />
      </AutoComplete>
    </div>
  ));
};

const CompoundCard = ({
  compound, onDelete, onSave,
}) => {
  const [value, setValue] = useState(compound);
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    setValue(compound);
  }, [compound]);

  return (
    <div className="PesticideCompounds__Card">
      {isEditing ? (
        <div className="PesticideCompounds__Card__Input">
          <Input.TextArea
            showCount
            defaultValue={value}
            value={value}
            maxLength={maxCompoundNameLength}
            onChange={(e) => setValue(e.target.value)}
            style={{ resize: "none" }} //eslint-disable-line
          />
          <div className="PesticideCompounds__Card__Buttons">
            <Button onClick={() => setIsEditing(false)} size="small">
              Cancel
            </Button>
            <Button disabled={!value} onClick={() => onSave(value, "edit", compound)} type="primary" size="small" loading={false}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <span>{compound}</span>
          <div className="PesticideCompounds__Card__Actions">
            <EditOutlined onClick={() => setIsEditing(true)} className="PesticideCompounds__Card--action" />
            <DeleteOutlined onClick={onDelete} className="PesticideCompounds__Card--action" />
          </div>
        </>
      )}
    </div>
  );
};

const AddCompoundCard = ({ onSave }) => {
  const [value, setValue] = useState("");
  const [addCompound, setAddCompound] = useState(false);

  if (!addCompound) {
    return (
      <div onClick={() => setAddCompound(true)} className="PesticideCompounds__AddButton">
        <PlusOutlined />
        <span>Add Compound</span>
      </div>
    );
  }
  return (
    <div className="PesticideCompounds__Card">
      <div className="PesticideCompounds__Card__Input">
        <Input.TextArea
          showCount
          placeholder="Enter Compound"
          value={value}
          maxLength={maxCompoundNameLength}
          onChange={(e) => setValue(e.target.value)}
          style={{ resize: "none" }} //eslint-disable-line
        />
        <div className="PesticideCompounds__Card__Buttons">
          <Button onClick={() => setAddCompound(false)} size="small">
            Cancel
          </Button>
          <Button disabled={!value} onClick={() => onSave(value, "add")} type="primary" size="small" loading={false}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function PesticideCompounds() {
  const [search, setSearch] = useState("");
  const [compounds, setCompounds] = useState([]);
  const [filteredCompounds, setFilteredCompounds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addCompoundKey, setAddCompoundKey] = useState(uuidv4());
  const [isMounted, setIsMounted] = useState(true); // Track mounted state

  async function fetchCompounds() {
    setLoading(true);
    const user = getUser();
    const payload = { action: "get", user_id: user.id };
    const { success, result, message } = await getPesticideCompounds(payload);
    if (!isMounted) return;
    if (success) {
      const newResult = result.map((item) => ({ ...item, key: uuidv4() }));
      setCompounds(newResult);
      setFilteredCompounds(search ? newResult.filter((item) => item.compound.toLowerCase().includes(search.toLowerCase())) : newResult);
    } else {
      toast.error(message);
    }
    setAddCompoundKey(uuidv4());
    setLoading(false);
  }

  /**
   * Fetch all compounds when landing, and track the mounted state for the current component
   */
  useEffect(() => {
    fetchCompounds();
    return () => { // when the component is unmounted, set the state to false to prevent further state updating
      setIsMounted(false);
    };
  }, []); // eslint-disable-line

  const handleSearch = (value) => {
    setSearch(value);
    const currCompounds = compounds.map((item) => ({ ...item, key: uuidv4() })); // provide a new key for each item to force all compound cards rerender
    if (!value) {
      setFilteredCompounds(currCompounds);
    } else {
      const filtered = currCompounds.filter((item) => item.compound.toLowerCase().includes(value.toLowerCase()));
      setFilteredCompounds(filtered);
    }
    setAddCompoundKey(uuidv4()); // provide a new key for the AddCompoundCard to force it rerender
  };

  const handleDelete = async (item) => {
    setLoading(true);
    const user = getUser();
    const payload = { action: "delete", user_id: user.id, compound: item.compound };
    const { success, message } = await deletePesticideCompounds(payload);
    if (!isMounted) return;
    if (success) {
      toast.success(message);
      fetchCompounds();
    } else {
      toast.error("Failed to delete Compound");
      setLoading(false);
    }
  };

  const handleSave = async (value, action, compound = null) => {
    setLoading(true);
    const user = getUser();
    let payload = {};
    if (action === "edit") {
      payload = {
        action, user_id: user.id, compound, new_compound: value,
      };
    }
    if (action === "add") {
      payload = { action, user_id: user.id, compound: value };
    }
    const { success, message } = await savePesticideCompounds(payload);
    if (!isMounted) return;
    if (success) {
      toast.success(message);
      fetchCompounds();
    } else {
      toast.error(`Failed to ${action} Compound`);
      setLoading(false);
    }
  };

  return (
    <div className="PesticideCompounds">
      <Header onSearch={handleSearch} compounds={compounds} />
      <Spin spinning={loading}>
        <div className="PesticideCompounds__Body">
          <AddCompoundCard key={addCompoundKey} onSave={handleSave} />
          {filteredCompounds.length === 0 && <div>No Data</div>}
          {filteredCompounds.length !== 0
            && filteredCompounds.map((item) => (
              <CompoundCard
                key={item.key}
                compound={item.compound}
                onDelete={() => handleDelete(item)}
                onSave={handleSave}
              />
            ))}
        </div>
      </Spin>
    </div>
  );
}
